import { cn } from "@/utils/cn";

type Tone = "primary" | "accent" | "secondary";

interface ProgressStatProps {
  label: string;
  detail: string;
  icon: string;
  /** Pourcentage entre 0 et 100. */
  value: number;
  tone?: Tone;
}

const iconTones: Record<Tone, string> = {
  primary: "bg-primary-50 text-primary-600",
  accent: "bg-accent-50 text-accent-700",
  secondary: "bg-secondary-50 text-secondary-600",
};

const barTones: Record<Tone, string> = {
  primary: "bg-primary-500",
  accent: "bg-accent-500",
  secondary: "bg-secondary-500",
};

/** Ligne d'indicateur avec une barre de progression (taux, objectif, occupation). */
export default function ProgressStat({ label, detail, icon, value, tone = "primary" }: ProgressStatProps) {
  const pct = Math.min(100, Math.max(0, Math.round(value)));

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-3">
          <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-md", iconTones[tone])}>
            <i className={cn(icon, "text-base")} aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-label font-medium text-foreground-900">{label}</span>
            <span className="block truncate text-xs text-foreground-500">{detail}</span>
          </span>
        </span>
        <span className="shrink-0 font-heading text-base font-semibold text-foreground-950">{pct} %</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-background-100">
        <div
          className={cn("h-full rounded-full transition-all duration-700", barTones[tone])}
          style={{ width: `${pct}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}